import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileCheck2 } from "lucide-react";
import { Reveal, Chapter } from "@/components/site/Reveal";
import { LIFECYCLE_PHASES } from "@/data/content";

export default function Lifecycle() {
  const [active, setActive] = useState(0);
  const phase = LIFECYCLE_PHASES[active];

  return (
    <section id="lifecycle" className="bg-ivory text-slateink">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-36">
        <Chapter n="Chapter 05" label="The Control Lifecycle" />
        <Reveal>
          <h2 className="max-w-3xl text-balance font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            A discipline for every phase of the production.
          </h2>
        </Reveal>

        <div data-testid="lifecycle-timeline" className="mt-16 grid gap-12 lg:grid-cols-12">
          <div className="flex gap-3 overflow-x-auto pb-2 lg:col-span-4 lg:flex-col lg:overflow-visible lg:pb-0">
            {LIFECYCLE_PHASES.map((p, i) => (
              <button
                key={p.id}
                data-testid={`lifecycle-tab-${i + 1}`}
                onClick={() => setActive(i)}
                className={`group flex shrink-0 items-center gap-4 border px-6 py-5 text-left transition-all duration-300 lg:w-full ${
                  active === i
                    ? "border-gold bg-ink text-ivory"
                    : "border-slateink/15 bg-transparent text-bodylight hover:border-gold/50"
                }`}
              >
                <span className={`font-mono text-[10px] tracking-[0.2em] ${active === i ? "text-gold" : "text-slateink/40"}`}>
                  0{i + 1}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em]">{p.label}</span>
              </button>
            ))}
          </div>

          <div className="relative min-h-[300px] border border-slateink/15 bg-white/50 p-9 sm:p-12 lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={phase.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              >
                <h3 className="font-serif text-2xl font-medium sm:text-3xl">{phase.title}</h3>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-bodylight sm:text-lg">{phase.body}</p>
                <div className="mt-10 flex items-start gap-4 border-t border-slateink/10 pt-7">
                  <FileCheck2 size={20} strokeWidth={1.5} className="mt-0.5 shrink-0 text-gold" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-bodylight">Key Deliverable</p>
                    <p className="mt-2 font-serif text-lg italic text-slateink">{phase.deliverable}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
